import {
  Box,
  Button,
  Flex,
  Heading,
  RadioCards,
  Spinner,
  Text,
} from "@radix-ui/themes";
import {
  refreshAccessToken,
  setInitializedStoreKey,
  updateGlobalAPIBaseURLs,
} from "@/shared/api/axios";
import {
  useLazyStaffStoreAppDetailsQuery,
  useStaffStoresQuery,
} from "@/shared/api";
import { logout } from "@/entities/session";
import { useState } from "react";
import { convertServicesToEnvironment } from "@/shared/lib/environment";
import { useAppDispatch } from "@/app/appStore";
import { useNavigate } from "react-router-dom";
import { getAppKey } from "@/shared/api/helpers";
import { ExitIcon } from "@radix-ui/react-icons";
import { appSession } from "@/shared/lib/storage";
import { useAuthRedirect } from "@/shared/hooks/useAuthRedirect";

export function Stores() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const appKey = getAppKey();
  const { data, isLoading } = useStaffStoresQuery({ appKey });
  const [loadingShop, setLoadingShop] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [getAppDetails] = useLazyStaffStoreAppDetailsQuery();
  const { redirectToLoginAfterLogout } = useAuthRedirect();
  const onSelect = async (storeKey: string) => {
    setLoadingShop(storeKey);

    try {
      const appDetails = await getAppDetails(
        { storeKey, appKey },
        false
      ).unwrap();

      if (appDetails) {
        await refreshAccessToken(storeKey);

        const services = convertServicesToEnvironment(
          appDetails.result.services
        );
        updateGlobalAPIBaseURLs(services);
        setInitializedStoreKey(storeKey);

        navigate(`/stores/${storeKey}`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingShop(null);
    }
  };

  const handleLogout = () => {
    dispatch(logout())
      .unwrap()
      .then(() => {
        setLoading(false);
        appSession.removeItem();
        redirectToLoginAfterLogout();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  return (
    <Flex height="100dvh" width="100%" justify="center" align="center">
      <Box className="space-y-4">
        <Heading align="center">Select store</Heading>

        <Button
          disabled={loading}
          className="absolute top-0 right-8"
          variant="ghost"
          color="gray"
          onClick={handleLogout}
        >
          <ExitIcon /> Logout
        </Button>

        <Box>
          {isLoading ? (
            <Flex justify="center" align="center" className="w-full">
              <Spinner />
            </Flex>
          ) : data?.results.length === 0 ? (
            <Flex justify="center" align="center" className="w-full">
              <Text color="gray">No stores found</Text>
            </Flex>
          ) : (
            <RadioCards.Root
              onValueChange={onSelect}
              className="flex flex-wrap gap-4 justify-center"
            >
              {data?.results.map((shop) => (
                <RadioCards.Item
                  value={shop.key}
                  key={shop.id}
                  className="min-w-[200px] basis-[calc(100%/3-1rem)]"
                >
                  {loadingShop === shop.key && (
                    <Flex
                      justify="center"
                      align="center"
                      className="absolute inset-0 bg-white/60"
                    >
                      <Spinner />
                    </Flex>
                  )}
                  <Flex direction="column" width="100%">
                    <Text weight="bold">{shop.name}</Text>
                    <Text>{shop.address}</Text>
                  </Flex>
                </RadioCards.Item>
              ))}
            </RadioCards.Root>
          )}
        </Box>
      </Box>
    </Flex>
  );
}
